/** Email one-time-code sign-in: wire shapes and hooks over the auth router's request-code, verify-code and otp-settings endpoints. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'

export interface CodeRequested {
  detail: string
}

/** `OtpSettingOut` on GET and `OtpSettingUpdate` on PUT are the same shape --
 *  every field is required either way, so one type serves both directions. */
export interface OtpSetting {
  is_enabled: boolean
  code_length: number
  ttl_seconds: number
  max_attempts: number
  /** Codes one address may ask for in a rolling hour; it makes `max_attempts` a real limit, since that budget is counted per issued code. */
  max_requests_per_hour: number
  allow_external_email: boolean
}

export function useRequestOtpCode() {
  return useMutation({
    mutationFn: async (email: string) =>
      (await api.post<CodeRequested>('/api/auth/request-code', { email })).data,
  })
}

/**
 * ⛔ THIS RETURNS NO TOKEN, AND MUST NOT BE MADE TO: `verify-code` delivers the session as an `HttpOnly` cookie, so no script on the page can read it.
 * The 200 is the success signal; `adoptOtpSession()` (./teams) must still be awaited afterwards because it enforces the one-identity rule and can refuse.
 */
export function useVerifyOtpCode() {
  return useMutation({
    mutationFn: async (payload: { email: string; code: string }) =>
      (await api.post<CodeRequested>('/api/auth/verify-code', payload)).data,
  })
}

export function useOtpSettings() {
  return useQuery({
    queryKey: ['otp-settings'],
    queryFn: async () => (await api.get<OtpSetting>('/api/auth/otp-settings')).data,
  })
}

export function useUpdateOtpSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (patch: OtpSetting) =>
      (await api.put<OtpSetting>('/api/auth/otp-settings', patch)).data,
    onSuccess: (data) => client.setQueryData(['otp-settings'], data),
  })
}
